import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { RecentTicket, RecentMessage } from '../../types/dashboard';

interface RecentActivityCardProps {
  tickets?: RecentTicket[];
  messages?: RecentMessage[];
}

export function RecentActivityCard({ tickets, messages }: RecentActivityCardProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'blue';
      case 'closed':
        return 'green';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'Aberto';
      case 'closed':
        return 'Fechado';
      case 'pending':
        return 'Pendente';
      default:
        return status;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {tickets && tickets.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tickets Recentes</h3>
          <div className="space-y-4">
            {tickets.slice(0, 5).map((ticket) => (
              <div key={ticket.id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-sm font-medium text-gray-900 flex-1 line-clamp-1">
                    {ticket.title}
                  </h4>
                  <Badge color={getStatusColor(ticket.status)}>
                    {getStatusLabel(ticket.status)}
                  </Badge>
                </div>
                {ticket.last_message && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                    {ticket.last_message}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(ticket.updated_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {messages && messages.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mensagens Recentes</h3>
          <div className="space-y-4">
            {messages.slice(0, 5).map((message) => (
              <div key={message.id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-500">
                    {message.session_id}
                  </span>
                  <Badge color={message.direction === 'received' ? 'green' : 'blue'}>
                    {message.direction === 'received' ? 'Recebida' : 'Enviada'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-900 line-clamp-2 mb-1">
                  {message.message}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(message.timestamp), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
